<template>
    <div class="cg">
        <p v-if="items.length < 2" class="cg-empty">{{ emptyText }}</p>

        <template v-else>
            <div class="cg-scroll">
                <table class="cg-table">
                    <thead>
                        <tr>
                            <th class="cg-corner"></th>
                            <th v-for="col in items" :key="col.id" class="cg-col-head">{{ col.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in items" :key="row.id">
                            <th class="cg-row-head">{{ row.label }}</th>
                            <td v-for="col in items" :key="col.id" class="cg-cell">
                                <span v-if="row.id === col.id" class="cg-diagonal" aria-hidden="true">–</span>
                                <button
                                    v-else
                                    type="button"
                                    class="cg-btn"
                                    :class="isBlocked(row.id, col.id) ? 'blocked' : 'allowed'"
                                    :title="cellTitle(row, col)"
                                    :aria-label="cellTitle(row, col)"
                                    @click="toggle(row.id, col.id)"
                                >{{ isBlocked(row.id, col.id) ? '✗' : '✓' }}</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="cg-legend">
                <span><b class="cg-legend-ok">✓</b> ניתן לשלב</span>
                <span><b class="cg-legend-no">✗</b> לא ניתן לשלב</span>
                <span class="cg-legend-hint">לחיצה על תא משנה את המצב · הטבלה סימטרית</span>
            </div>
        </template>
    </div>
</template>

<script>
import { pairKey } from '@/Methods/job_ads_options'

/**
 * Symmetric "can these two be combined?" grid.
 * - `items`:      [{ id, label }] – regions (branches) or roles
 * - `modelValue`: array of blocked pair keys ('a|b', sorted) – empty = everything allowed
 */
export default {
    name: 'CompatibilityGrid',
    props: {
        items: { type: Array, default: () => [] },
        modelValue: { type: Array, default: () => [] },
        emptyText: { type: String, default: 'יש להציע לפחות שתי אפשרויות כדי להגדיר הגבלות שילוב.' }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const isBlocked = (a, b) => props.modelValue.includes(pairKey(a, b))

        const toggle = (a, b) => {
            const key = pairKey(a, b)
            const next = isBlocked(a, b)
                ? props.modelValue.filter(k => k !== key)
                : [...props.modelValue, key]
            emit('update:modelValue', next)
        }

        const cellTitle = (row, col) =>
            `${row.label} + ${col.label}: ${isBlocked(row.id, col.id) ? 'לא ניתן לשלב' : 'ניתן לשלב'}`

        return { isBlocked, toggle, cellTitle }
    }
}
</script>

<style scoped>
.cg {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.cg-empty {
    margin: 0;
    font-size: 13px;
    color: #888;
}
.cg-scroll {
    overflow-x: auto;
}
.cg-table {
    border-collapse: separate;
    border-spacing: 4px;
    margin: 0 auto;
}
.cg-corner {
    width: 90px;
}
.cg-col-head,
.cg-row-head {
    font-size: 12px;
    font-weight: 700;
    color: #444;
    padding: 4px 6px;
    line-height: 1.25;
}
.cg-col-head {
    vertical-align: bottom;
    text-align: center;
    max-width: 72px;
    white-space: normal;
}
.cg-row-head {
    text-align: right;
    white-space: nowrap;
}
.cg-cell {
    text-align: center;
    padding: 0;
}
.cg-diagonal {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 40px;
    color: #ccc;
    background: #f5f5f5;
    border-radius: 8px;
}
.cg-btn {
    width: 44px;
    height: 40px;
    border-radius: 8px;
    border: 1.5px solid transparent;
    font-size: 17px;
    font-weight: 900;
    cursor: pointer;
    font-family: inherit;
    transition: transform 0.1s, background 0.15s;
}
.cg-btn:hover {
    transform: scale(1.06);
}
.cg-btn.allowed {
    background: #e8f5e9;
    color: #2e7d32;
    border-color: #c8e6c9;
}
.cg-btn.blocked {
    background: #fdecea;
    color: #bb2429;
    border-color: #f5c2c0;
}
.cg-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    justify-content: center;
    font-size: 12px;
    color: #666;
}
.cg-legend-ok {
    color: #2e7d32;
}
.cg-legend-no {
    color: #bb2429;
}
.cg-legend-hint {
    color: #999;
}
</style>
